import { Fragment, useId } from "react";
import {
  ArrowsRightLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Input } from "@sablier/v2-components/organisms";
import { DEFAULT_DECIMALS } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import {
  useFieldAddress,
  useFieldAmount,
  useFieldCancelability,
  useFieldDuration,
  useFieldEnd,
  useFieldShape,
  useFieldStart,
  useFieldStreams,
  useFieldToken,
  useTimeFlip,
} from "../logic";
import Item from "./Item";

function Shape() {
  const { shape } = useFieldShape();
  const { t } = useT();

  return (
    <Input.LockedShape
      id={shape.field.id}
      label={{ value: t("structs.streamingModel") }}
      value={shape.field.value}
    />
  );
}

function Cancelability() {
  const { cancelability, value } = useFieldCancelability();
  const { t } = useT();

  return (
    <Input.Switch
      id={cancelability.field.id}
      label={{
        value: t("form.label.cancelability"),
        isIconLast: true,
        icon: InformationCircleIcon,
        tooltip: {
          value: t("tooltips.cancelability"),
        },
      }}
      onChange={(checked) => cancelability.update({ value: checked })}
      value={value}
      warning={cancelability.field.warning}
    />
  );
}

function Token() {
  const { token, onChange } = useFieldToken();
  const { t } = useT();

  return (
    <Input.TokenPicker
      id={token.field.id}
      onChange={onChange}
      label={{ value: _.capitalize(t("words.token")) }}
      placeholder={t("form.placeholder.token")}
      value={{ raw: token.field.value }}
      warning={token.field.warning}
    />
  );
}

function Streams() {
  const { streams } = useFieldStreams();

  return (
    <Fragment>
      {streams.field.value &&
        streams.field.value.map((item, index) => {
          return <Item key={index} index={index} streamId={item.id} />;
        })}
    </Fragment>
  );
}

interface Props {
  streamId: string;
}

function Address({ streamId }: Props) {
  const { address, isLoading, label, onBlur, onChange, onFocus } =
    useFieldAddress(streamId);
  const { t } = useT();

  return (
    <Input.Address
      id={`${address.field.id}-${streamId}`}
      isLoading={isLoading}
      info={isLoading && t("form.placeholder.resolving")}
      label={label}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={t("form.placeholder.address")}
      resolution={address.field.resolution?.short}
      value={address.field.value}
      warning={!isLoading && address.field.warning}
    />
  );
}
function Amount({ streamId }: Props) {
  const { amount, maximum, onBlur, onChange, onMax, token } =
    useFieldAmount(streamId);
  const { t } = useT();

  return (
    <Input.Amount
      id={`${amount.field.id}-${streamId}`}
      decimals={token.field.value?.decimals || DEFAULT_DECIMALS}
      isLoading={amount.field.isLoading}
      label={{ value: t("words.amount") }}
      max={maximum}
      onBlur={onBlur}
      onChange={onChange}
      onMax={onMax}
      placeholder={t("form.placeholder.amount")}
      token={token.field.value}
      value={amount.field.value}
      warning={amount.field.warning}
    />
  );
}

function Duration({ streamId }: Props) {
  const { duration, onClick } = useFieldDuration(streamId);
  const { toMoment } = useTimeFlip(streamId);
  const { t } = useT();

  return (
    <Input.Duration
      id={`${duration.field.id}-${streamId}`}
      flip={toMoment}
      label={{ value: t("words.duration") }}
      onClick={onClick}
      placeholder={t("form.placeholder.duration")}
      value={duration.field.value}
      warning={duration.field.warning}
    />
  );
}

function End({ streamId }: Props) {
  const { end, placeholder, onClick } = useFieldEnd(streamId);
  const { t } = useT();

  return (
    <Input.Moment
      id={`${end.field.id}-${streamId}`}
      label={{ value: t("structs.endDate") }}
      onClick={onClick}
      placeholder={placeholder}
      value={end.field.value}
      warning={end.field.warning}
    />
  );
}

function Start({ streamId }: Props) {
  const { onClick, placeholder, start } = useFieldStart(streamId);
  const { t } = useT();

  return (
    <Input.Moment
      id={`${start.field.id}-${streamId}`}
      label={{ value: t("structs.startDate") }}
      onClick={onClick}
      placeholder={placeholder}
      value={start.field.value}
      warning={start.field.warning}
    />
  );
}

function Switch({ streamId }: Props) {
  const { toDuration } = useTimeFlip(streamId);

  const id = useId();
  const { t } = useT();

  return (
    <Input.Action
      id={`${id}-duration-switch`}
      label={{ value: " " }}
      value={{
        accent: "input",
        appearance: "solid",
        isMini: true,
        onClick: toDuration.onClick,
        left: ArrowsRightLeftIcon,
        title: t("words.duration"),
        titleShort: "",
      }}
    />
  );
}

const Fields = {
  Shape,
  Cancelability,
  Token,
  Streams,
  Address,
  Amount,
  Duration,
  End,
  Start,
  Switch,
};

export default Fields;
